import { owlWindowBounds } from "../../supabase/functions/_shared/owl-window.ts";
// useOwlWatchList — fallback when zero OWL tiers clear the gate.
//
// Per OWL-F3 founder ruling: never show a dead page. When the pipeline
// returns empty for every tier, surface 3-5 fixtures we're TRACKING
// (with their edge / conviction / market read) clearly labelled as
// READ-only — never a BET button. The user always has intel even when
// no edge clears for a real publish.
//
// Reads vw_substrate_fixtures directly via the authenticated client
// (same view Singles/Acca consume). Window filter is the OWL window
// (versioned session boundaries; new sessions run 19:00–07:00 Accra).
// Sorted by substrate fair_prob × |clv_gap| so we surface the most
// confident reads first, even when none cleared the strict edge gate.

import { useEffect, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type OwlMarketKind = "match_winner" | "ou" | "btts";

/** Honesty hierarchy for every fixture in the OWL window.
 *  BET   — fair edge ≥ a publishable threshold (matches the per-tier
 *           edge floor used by the BET-product pipeline).
 *  MODEL — fair prediction with edge near zero. The agents' best read,
 *           NO edge claim. Read-only, no Lock CTA.
 *  PASS  — negative edge. Market is right; we don't disagree. Shown
 *           greyed for transparency. */
export type OwlPredictionKind = "BET" | "MODEL" | "PASS";

export interface OwlWatchItem {
  fixture_id: string;
  home_team: string;
  away_team: string;
  competition: string;
  kickoff_at: string;
  market: OwlMarketKind;
  selection: string;
  fair_prob: number;
  fair_odds: number;
  market_odds: number | null;
  clv_gap: number | null;
  conviction_tier: string | null;
  /** OWL-M1 — classification surfaced to the UI. */
  prediction_kind: OwlPredictionKind;
}

/** Edge floors used to classify a row. Kept loose so MODEL contains
 *  most fixtures and BET is reserved for genuine edge. Pipeline edge
 *  gates (per-tier 2.0-4.0%) sit ABOVE this — a BET-classified row
 *  here may still not clear an aggressive-tier publish. */
const BET_EDGE_FLOOR  = 0.020;   // 2.0% — same as rollover/moderate min
const PASS_EDGE_CEILING = -0.010; // -1.0% — anything below = market is right

export function classifyPrediction(clv_gap: number | null): OwlPredictionKind {
  const g = clv_gap ?? 0;
  if (g >= BET_EDGE_FLOOR) return "BET";
  if (g <= PASS_EDGE_CEILING) return "PASS";
  return "MODEL";
}

export interface UseOwlWatchListState {
  items: readonly OwlWatchItem[];
  loading: boolean;
  error: string | null;
}

function humanSelection(market: OwlMarketKind, sel: string, home: string, away: string): string {
  switch (market) {
    case "match_winner":
      if (sel === "home") return home;
      if (sel === "away") return away;
      return "Draw";
    case "ou":
      if (sel === "over") return "Over 2.5";
      if (sel.startsWith("over_")) return `Over ${sel.slice(5).replace("_", ".")}`;
      return "Unavailable";
    case "btts":
      return sel === "yes" ? "BTTS Yes" : "BTTS No";
  }
}

function humanMarket(market: OwlMarketKind): string {
  switch (market) {
    case "match_winner": return "Match Result";
    case "ou":           return "Total Goals";
    case "btts":         return "Both Teams To Score";
  }
}

export function useOwlWatchList(sessionDate: string | undefined, limit = 50): UseOwlWatchListState {
  const { user } = useAuthSession();
  const [items, setItems] = useState<readonly OwlWatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !sessionDate) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      // This regional view excludes non-OWL competitions before ranking.
      const nextDate = new Date(`${sessionDate}T00:00:00Z`);
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
      const {start:winStart,end:winEnd} = owlWindowBounds(sessionDate,"noon");

      // The OWL-scoped view isn't in generated typings — escape hatch
      // through `as never`, same pattern as useOwlSystemTrackRecord.
      const { data, error: e } = await (
        client.from("vw_owl_substrate_fixtures" as never) as unknown as {
          select: (cols: string) => {
            gte: (col: string, val: string) => {
              lt: (col: string, val: string) => {
                eq: (col: string, val: string) => {
                  in: (col: string, vals: string[]) => Promise<{ data: Array<Record<string, unknown>> | null; error: { message: string } | null }>;
                };
              };
            };
          };
        }
      )
        .select("fixture_id, home_team, away_team, competition, kickoff_at, market, selection, fair_prob, fair_odds, market_odds, clv_gap, conviction_tier")
        .gte("kickoff_at", winStart)
        .lt("kickoff_at", winEnd)
        .eq("sport", "football")
        .in("market", ["match_winner", "ou", "btts"]);

      if (cancelled) return;
      if (e) {
        setError(e.message);
        setLoading(false);
        return;
      }

      // Collapse to best selection per fixture by fair_prob × |clv_gap|.
      const best = new Map<string, { row: any; score: number }>();
      for (const r of (data ?? [])) {
        const market = String(r.market ?? "");
        const selection = String(r.selection ?? "");
        const attacking =
          (market === "match_winner" && (selection === "home" || selection === "away")) ||
          (market === "btts" && selection === "yes") ||
          (market === "ou" && (selection === "over" || selection.startsWith("over_")));
        if (!attacking) continue;
        const fp = Number(r.fair_prob ?? 0);
        const gap = Math.abs(Number(r.clv_gap ?? 0));
        const score = fp * (1 + gap * 2); // weight value when present
        const fid = String(r.fixture_id);
        const cur = best.get(fid);
        if (!cur || score > cur.score) best.set(fid, { row: r, score });
      }

      // OWL-M1 — order: BET-classified first (by edge), then MODEL by
      // fair_prob (most confident reads), then PASS at the bottom for
      // full transparency. Don't pre-truncate before classification or
      // we'll bias the cut against low-edge but high-confidence reads.
      const allRanked = [...best.values()]
        .map(({ row: r }): OwlWatchItem => {
          const market = String(r.market ?? "match_winner") as OwlMarketKind;
          const home = String(r.home_team ?? "Home");
          const away = String(r.away_team ?? "Away");
          const sel = String(r.selection ?? "");
          const gap = r.clv_gap != null ? Number(r.clv_gap) : null;
          return {
            fixture_id: String(r.fixture_id ?? ""),
            home_team: home,
            away_team: away,
            competition: String(r.competition ?? ""),
            kickoff_at: String(r.kickoff_at ?? ""),
            market,
            selection: humanSelection(market, sel, home, away),
            fair_prob: Number(r.fair_prob ?? 0),
            fair_odds: Number(r.fair_odds ?? 0),
            market_odds: r.market_odds != null ? Number(r.market_odds) : null,
            clv_gap: gap,
            conviction_tier: (r.conviction_tier as string) ?? null,
            prediction_kind: classifyPrediction(gap),
          };
        });

      // Sort: BET (by edge desc) → MODEL (by fair_prob desc) → PASS (by kickoff asc).
      const ranked = allRanked.sort((a, b) => {
        const ka = a.prediction_kind, kb = b.prediction_kind;
        if (ka !== kb) {
          const order: Record<OwlPredictionKind, number> = { BET: 0, MODEL: 1, PASS: 2 };
          return order[ka] - order[kb];
        }
        if (ka === "BET")  return (b.clv_gap ?? 0) - (a.clv_gap ?? 0);
        if (ka === "MODEL") return b.fair_prob - a.fair_prob;
        return a.kickoff_at.localeCompare(b.kickoff_at);
      }).slice(0, limit);

      setItems(ranked);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, sessionDate, limit]);

  return { items, loading, error };
}

export { humanMarket };
